import React, { useCallback, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, AlertTriangle, SkipForward, ShoppingBag, PackagePlus, ArrowRight } from "lucide-react";

/**
 * AdminNiftyImport — round-trip CSV importers for the two Nifty exports.
 *
 * Nifty has no public API and no webhooks (per Nifty AI, Sept 2026). The
 * only supported inbound channel is CSV export from Analytics → Reports.
 * This page accepts those CSVs and previews the exact effect before commit:
 *
 *   • Active Inventory CSV → new inventory records for SKUs not yet in MP.
 *     Kills the daily "retype title from Nifty into MP" pain.
 *
 *   • Orders Report CSV → marks matching MP items sold with the exact
 *     marketplace, sale price, per-fee breakdown (standard/shipping/promoted),
 *     shipping-expense, buyer-state and tax-remitted flag Nifty provides —
 *     so the monthly P&L stays current without her retyping a single field.
 *
 * Idempotency: skip-existing. Re-uploading the same CSV is a no-op.
 */

const TAB_LISTINGS = "listings";
const TAB_ORDERS = "orders";

export default function AdminNiftyImport() {
  const [tab, setTab] = useState(TAB_LISTINGS);
  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-6xl">
        <header>
          <p className="editorial-label mb-1">phase 1 · nifty ↔ maison pilet</p>
          <h1 className="font-serif text-4xl text-charcoal">import from nifty</h1>
          <p className="text-espresso mt-2 max-w-3xl text-sm">
            drop a nifty csv below to sync into maison pilet. inventory imports create skeleton records for new SKUs;
            orders imports mark matching pieces sold and record every fee marketplace-by-marketplace so the p&amp;l stays current.
            duplicate imports are safe — existing SKUs are skipped, not overwritten.
          </p>
          <div className="mt-3 bg-linen border border-taupe/50 px-3 py-2 text-xs text-espresso flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-brass flex-shrink-0 mt-0.5" />
            <span>
              <strong>tip:</strong> import <em>listings</em> before <em>orders</em>. orders match by SKU — if an order references
              a SKU that isn't in mp yet, the row lands in the errors bucket and gets skipped.
            </span>
          </div>
        </header>

        {/* Tab switcher */}
        <div className="flex gap-1 border-b border-taupe/40">
          <TabButton active={tab === TAB_LISTINGS} onClick={() => setTab(TAB_LISTINGS)} icon={PackagePlus} label="listings" testId="nifty-tab-listings" />
          <TabButton active={tab === TAB_ORDERS} onClick={() => setTab(TAB_ORDERS)} icon={ShoppingBag} label="orders" testId="nifty-tab-orders" />
        </div>

        {tab === TAB_LISTINGS ? (
          <ImportPanel
            kind="listings"
            previewEndpoint="/nifty/import-inventory/preview"
            commitEndpoint="/nifty/import-inventory/commit"
            columns={[
              { key: "sku", label: "SKU", w: "w-20", fmt: (v) => v || "(auto)" },
              { key: "title", label: "Item Name", w: "flex-1" },
              { key: "purchase_cost", label: "Nifty COGS", w: "w-24", fmt: (v) => v ? `$${Number(v).toFixed(2)}` : "—" },
              { key: "listing_date", label: "Nifty Listed", w: "w-40", fmt: (v) => v ? v.slice(0, 10) : "—" },
            ]}
            emptyHint="drop nifty's active inventory csv here (analytics → reports → export). rows without a Nifty SKU get a fresh MP SKU auto-assigned; rows whose SKU or title already exists are skipped."
            newLabel="will be created in mp"
            skipLabel="already in mp — skipped"
          />
        ) : (
          <ImportPanel
            kind="orders"
            previewEndpoint="/nifty/import-orders/preview"
            commitEndpoint="/nifty/import-orders/commit"
            columns={[
              { key: "sku", label: "SKU", w: "w-16" },
              { key: "marketplace", label: "Where", w: "w-20", fmt: (v) => (v || "").toLowerCase() },
              { key: "sold_at", label: "Sold At", w: "w-40", fmt: (v) => v ? v.slice(0, 16) : "—" },
              { key: "sale_price", label: "Sale $", w: "w-20", fmt: (v) => `$${Number(v || 0).toFixed(2)}` },
              { key: "sale_payment_processing_fee", label: "Fees", w: "w-20", fmt: (v) => `$${Number(v || 0).toFixed(2)}` },
              { key: "total_profit_nifty", label: "Net (Nifty)", w: "w-24", fmt: (v) => `$${Number(v || 0).toFixed(2)}` },
            ]}
            emptyHint="drop nifty's orders report csv here (analytics → reports → export)."
            newLabel="will be marked sold in mp"
            skipLabel="already sold — skipped"
          />
        )}
      </div>
    </AdminLayout>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2.5 text-xs tracking-widest uppercase flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-brass text-charcoal" : "border-transparent text-taupe hover:text-espresso"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

/**
 * ImportPanel — a self-contained upload → preview → commit flow.
 *
 * States: idle → previewing → previewed → committing → committed. On any
 * error we drop back to `idle` so the user can just re-drop the file.
 */
function ImportPanel({ kind, previewEndpoint, commitEndpoint, columns, emptyHint, newLabel, skipLabel }) {
  const [state, setState] = useState("idle"); // idle | previewing | previewed | committing | committed
  const [preview, setPreview] = useState(null); // { new, skip, errors, total }
  const [committed, setCommitted] = useState(null); // { created|updated, skipped }
  const [dragOver, setDragOver] = useState(false);
  // Opt-in for the listings tab only: refresh title + COGS on already-existing
  // MP records with Nifty's latest values. Default OFF because create-only
  // is the safe daily-run behavior.
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const fileInputRef = useRef(null);

  const runPreview = useCallback(async (file) => {
    if (!file) return;
    setState("previewing");
    setPreview(null);
    setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(previewEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setState("previewed");
    } catch (e) {
      toast.error(`preview failed: ${e.response?.data?.detail || e.message}`);
      setState("idle");
    }
  }, [previewEndpoint]);

  const runCommit = useCallback(async () => {
    if (!preview?.new?.length && !(overwriteExisting && preview?.skip?.some((r) => r.updatable))) return;
    setState("committing");
    try {
      // When overwrite is on, send the updatable skip rows alongside the new
      // rows — the commit endpoint refreshes title/COGS on the existing MP
      // records for those SKUs.
      const skipRows = overwriteExisting ? (preview.skip || []).filter((r) => r.updatable) : [];
      const { data } = await api.post(commitEndpoint, {
        rows: [...preview.new, ...skipRows],
        overwrite_existing: overwriteExisting,
      });
      setCommitted(data);
      setState("committed");
      const verb = kind === "listings" ? "created" : "updated";
      const n = data.created ?? data.updated ?? 0;
      const up = data.updated ?? 0;
      const msg = kind === "listings" && overwriteExisting && up > 0
        ? `${n} created, ${up} refreshed.`
        : `${n} ${kind === "listings" ? "listing" : "sale"}${n === 1 ? "" : "s"} ${verb}.`;
      toast.success(msg);
    } catch (e) {
      toast.error(`commit failed: ${e.response?.data?.detail || e.message}`);
      setState("previewed");
    }
  }, [preview, commitEndpoint, kind, overwriteExisting]);

  const reset = () => { setPreview(null); setCommitted(null); setState("idle"); };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) runPreview(f);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) runPreview(f);
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      {(state === "idle" || state === "previewing") && (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${
            dragOver ? "border-brass bg-linen" : "border-taupe/50 hover:border-taupe bg-ivory/50"
          }`}
          data-testid={`nifty-${kind}-dropzone`}
        >
          {state === "previewing" ? (
            <div className="flex flex-col items-center gap-3 text-taupe">
              <Loader2 className="w-8 h-8 animate-spin text-brass" />
              <p className="editorial-label">parsing csv…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-taupe">
              <Upload className="w-8 h-8 text-brass" />
              <p className="editorial-label text-charcoal">drop or choose a csv</p>
              <p className="text-xs text-taupe max-w-md">{emptyHint}</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFileChange}
            data-testid={`nifty-${kind}-file-input`}
          />
        </div>
      )}

      {/* Preview */}
      {state === "previewed" && preview && (
        <PreviewSection
          preview={preview}
          columns={columns}
          newLabel={newLabel}
          skipLabel={skipLabel}
          onCancel={reset}
          onCommit={runCommit}
          kind={kind}
          overwriteExisting={overwriteExisting}
          onOverwriteChange={setOverwriteExisting}
        />
      )}

      {/* Committing spinner */}
      {state === "committing" && (
        <div className="border border-taupe/50 p-8 text-center text-taupe flex items-center justify-center gap-3" data-testid={`nifty-${kind}-committing`}>
          <Loader2 className="w-5 h-5 animate-spin text-brass" />
          <span className="text-sm">committing to maison pilet…</span>
        </div>
      )}

      {/* Committed summary */}
      {state === "committed" && committed && (
        <div className="border border-emerald-500/60 bg-emerald-50 p-6 flex items-start gap-3" data-testid={`nifty-${kind}-committed`}>
          <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-charcoal font-medium">
              {kind === "listings"
                ? `${committed.created} new inventory record${committed.created === 1 ? "" : "s"} created in maison pilet.`
                : `${committed.updated} sale${committed.updated === 1 ? "" : "s"} recorded — accounting is current.`}
            </p>
            {committed.skipped > 0 && (
              <p className="text-xs text-taupe mt-1">{committed.skipped} row{committed.skipped === 1 ? "" : "s"} skipped (already in mp).</p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={reset}
                className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
                data-testid={`nifty-${kind}-reset`}
              >
                import another csv <ArrowRight className="w-3 h-3" />
              </button>
              {kind === "listings" && committed.created > 0 && (
                // Photo backfill shortcut: Nifty CSV has no photo URLs (Nifty
                // exports don't expose them), so the pragmatic path is to
                // send her straight to the Bulk Photo Sorter where she can
                // drop the same iPhone photos and drag-assign to the freshly
                // imported skeleton SKUs.
                <a
                  href="/admin/inventory/bulk-photos"
                  className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
                  data-testid="nifty-listings-add-photos"
                >
                  add photos to these {committed.created} piece{committed.created === 1 ? "" : "s"} <ArrowRight className="w-3 h-3" />
                </a>
              )}
              {kind === "orders" && committed.updated > 0 && (
                <a
                  href="/admin/analytics/sourcing"
                  className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
                  data-testid="nifty-orders-view-roi"
                >
                  see sourcing ROI <ArrowRight className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewSection({ preview, columns, newLabel, skipLabel, onCancel, onCommit, kind, overwriteExisting, onOverwriteChange }) {
  const updatableCount = kind === "listings" ? (preview.skip || []).filter((r) => r.updatable).length : 0;
  const commitCount = preview.new.length + (overwriteExisting ? updatableCount : 0);
  return (
    <div className="space-y-4" data-testid={`nifty-${kind}-preview`}>
      {/* Summary counters */}
      <div className="grid grid-cols-3 gap-3">
        <Counter label={newLabel} count={preview.new.length} tone="brass" testId={`nifty-${kind}-new-count`} />
        <Counter label={skipLabel} count={preview.skip.length} tone="taupe" icon={SkipForward} testId={`nifty-${kind}-skip-count`} />
        <Counter label="needs manual attention" count={preview.errors.length} tone="red" icon={AlertTriangle} testId={`nifty-${kind}-error-count`} />
      </div>

      {/* Overwrite-existing opt-in — only for listings tab */}
      {kind === "listings" && updatableCount > 0 && (
        <label
          className="flex items-start gap-3 border border-brass/50 bg-linen p-3 cursor-pointer"
          data-testid="nifty-overwrite-toggle"
        >
          <input
            type="checkbox"
            checked={overwriteExisting}
            onChange={(e) => onOverwriteChange(e.target.checked)}
            className="mt-0.5 accent-brass"
            data-testid="nifty-overwrite-checkbox"
          />
          <div className="text-xs">
            <p className="text-charcoal">
              <strong>also refresh titles + COGS on the {updatableCount} skipped record{updatableCount === 1 ? "" : "s"}</strong> with nifty's latest values
            </p>
            <p className="text-taupe mt-0.5">
              only touches title and purchase cost. photos, sale price, stage, sold status and sourcing trip link are never overwritten.
            </p>
          </div>
        </label>
      )}

      {/* Actions */}
      <div className="flex gap-2 items-center">
        <button
          onClick={onCommit}
          disabled={commitCount === 0}
          className="border border-brass bg-brass text-ivory hover:bg-charcoal hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-[0.65rem] tracking-widest uppercase"
          data-testid={`nifty-${kind}-commit`}
        >
          {commitCount > 0
            ? `commit ${commitCount} row${commitCount === 1 ? "" : "s"}${overwriteExisting && updatableCount > 0 ? ` (${preview.new.length} new + ${updatableCount} refresh)` : ""}`
            : "nothing to commit"}
        </button>
        <button
          onClick={onCancel}
          className="border border-taupe hover:border-charcoal px-4 py-2 text-[0.65rem] tracking-widest uppercase"
          data-testid={`nifty-${kind}-cancel`}
        >
          cancel
        </button>
        <span className="text-xs text-taupe">total rows in csv: {preview.total}</span>
      </div>

      {/* New rows preview */}
      {preview.new.length > 0 && (
        <details open className="border border-brass/60 bg-linen/50">
          <summary className="cursor-pointer px-4 py-2 text-[0.65rem] tracking-widest uppercase text-charcoal font-medium">
            {preview.new.length} row{preview.new.length === 1 ? "" : "s"} · {newLabel}
          </summary>
          <RowTable rows={preview.new} columns={columns} kind={kind} bucket="new" />
        </details>
      )}

      {/* Errors — always expanded because they need attention */}
      {preview.errors.length > 0 && (
        <details open className="border border-red-500/50 bg-red-50/50">
          <summary className="cursor-pointer px-4 py-2 text-[0.65rem] tracking-widest uppercase text-red-800 font-medium flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> {preview.errors.length} row{preview.errors.length === 1 ? "" : "s"} · manual reconciliation needed
          </summary>
          <RowTable rows={preview.errors} columns={[...columns.slice(0, 2), { key: "reason", label: "why", w: "flex-1" }]} kind={kind} bucket="error" />
        </details>
      )}

      {/* Skipped — collapsed, informational only */}
      {preview.skip.length > 0 && (
        <details className="border border-taupe/40 bg-ivory">
          <summary className="cursor-pointer px-4 py-2 text-[0.65rem] tracking-widest uppercase text-taupe">
            {preview.skip.length} row{preview.skip.length === 1 ? "" : "s"} · {skipLabel}
          </summary>
          <RowTable rows={preview.skip} columns={columns.slice(0, 3)} kind={kind} bucket="skip" />
        </details>
      )}
    </div>
  );
}

function Counter({ label, count, tone, icon: Icon, testId }) {
  const toneCls = {
    brass: "border-brass/60 bg-linen text-brass",
    taupe: "border-taupe/50 bg-ivory text-taupe",
    red: "border-red-500/50 bg-red-50 text-red-800",
  }[tone];
  return (
    <div className={`border p-4 ${toneCls}`} data-testid={testId}>
      <p className="text-3xl font-serif">{count}</p>
      <p className="text-[0.6rem] tracking-widest uppercase mt-1">{label}</p>
      {Icon && count > 0 && <Icon className="w-3 h-3 mt-2" />}
    </div>
  );
}

function RowTable({ rows, columns, kind, bucket }) {
  return (
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-xs" data-testid={`nifty-${kind}-${bucket}-table`}>
        <thead className="bg-ivory sticky top-0 border-b border-taupe/40">
          <tr className="text-[0.6rem] tracking-widest uppercase text-taupe">
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-left ${c.w || ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((r, i) => (
            <tr key={i} className="border-b border-taupe/20 hover:bg-linen/30">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-1.5 ${c.w || ""}`}>
                  {c.fmt ? c.fmt(r[c.key]) : (r[c.key] || "—")}
                </td>
              ))}
            </tr>
          ))}
          {rows.length > 200 && (
            <tr><td colSpan={columns.length} className="px-3 py-2 text-taupe italic text-xs">…and {rows.length - 200} more (shown in commit).</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
